import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { IconSet } from 'stile-shared/src/components/2024/common/IconSet/IconSet';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import PedagogyHeader from 'stile-shared/src/components/2024/common/PedagogyHeader';
import PedagogyWithinStileCard from 'stile-shared/src/components/2024/common/PedagogyWithinStileCard';
import { SubNav, SubNavItem } from 'stile-shared/src/components/2024/common/SubNav';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

const ExamplesSection = styled<typeof Section>(Section)`
  margin-top: 0 !important;
  grid-column: span 12;

  .content {
    padding-top: 0 !important;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      padding-top: 0 !important;
      margin-bottom: 69px !important;
      margin: 0 25px;
    }
  }

  position: relative;

  h6 {
    color: var(--stile-green-3);
    margin: 0;
    margin-bottom: 10px;
    grid-column: span 12;
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
  }

  .grid-layout {
    margin-top: 0 !important;
    grid-column: 1 / span 12;
  }

  > .grid-layout {
    align-items: center;
    margin-bottom: 150px;
    @media (max-width: 768px) {
      margin-bottom: 80px;
    }
  }
`;

export { Head } from 'templates/2024/Head';

function CognitiveLoadTheoryPage(props: PageProps) {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  const subNavItems: SubNavItem[] = [
    {
      label: '5E Instructional Model',
      url: '/pedagogies/5e-instructional-model/',
    },
    {
      label: 'SOLO Taxonomy',
      url: '/pedagogies/solo-taxonomy/',
    },
    {
      label: 'Explicit Instruction',
      url: '/pedagogies/explicit-instruction/',
    },
    {
      label: 'Cognitive Load Theory',
      url: '/pedagogies/cognitive-load-theory/',
      isActive: true,
    },
    {
      label: 'Differentiated Instruction',
      url: '/pedagogies/differentiated-instruction/',
    },
  ];

  return (
    <PageLayout {...props}>
      <SubNav items={subNavItems} />

      <PedagogyHeader
        title="Cognitive load theory"
        description="Cognitive load theory (CLT) explains that students have limited working memory, so lessons should be structured to optimise how much students need to draw from it. New information should be chunked, clearly presented and linked to prior knowledge. When used effectively, CLT helps students to use their mental focus on learning and transferring ideas to long-term memory."
        iconBullets={[
          {
            iconSrc: IconSet['lightonIcon0404'],
            title: 'Initially developed by',
            text: 'John Sweller, University of New South Wales, Australia (1988).',
          },
          {
            iconSrc: IconSet['conflictIcon0505'],
            title: 'Effectiveness',
            text: (
              <>
                <Link url="https://psycnet.apa.org/buy/2015-01780-001" openInNewTab>
                  Sweller et al.’s 2015 Randomized Controlled Trial
                </Link>{' '}
                showed that students taught complex tasks with the help of worked examples
                significantly outperformed the control group, with effect sizes ranging from 0.8 to
                1.2.
              </>
            ),
          },
          {
            iconSrc: IconSet['glossaryIcon0705'],
            title: 'Further reading',
            text: (
              <Link
                url="https://books.google.com.au/books/about/Cognitive_Load_Theory.html?id=sSAwbd8qOAAC"
                openInNewTab
              >
                Cognitive Load Theory by Sweller, Ayres and Kalyuga. 2011
              </Link>
            ),
          },
          {
            iconSrc: IconSet['locationIcon0704'],
            title: "Where you'll find it in Stile",
            text: 'Cognitive load theory is used as a design principle across all of Stile’s lessons.',
          },
        ]}
      />

      <ExamplesSection>
        <h6>Examples within Stile</h6>
        <Grid>
          <PedagogyWithinStileCard
            id="front-loading-scientific-terminology"
            title="Front-loading scientific terminology"
            description="Key scientific terms are introduced early in lessons. Definitions are paired with a reference image and can be read aloud to students. The same images and definitions are used in printable flash cards, available for each unit."
            url="http://stileapp.com/go/cognitive_example_1"
            subtitle={
              <div>
                <em>The role of gravity</em> lesson
                <br />
                in the Our Place in Space unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/cognitive_front_loading_AU.png"
                alt="Visuals of galaxy formation and solar system diagrams with text explaining the Sun and orbiting objects like planets, moons, asteroids, and comets."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="fully-worked-model-answers"
            title="Fully worked model answers"
            description="Every question has a complete model answer, so the process of working through a question can be clearly demonstrated to students."
            url="http://stileapp.com/go/cognitive_example_2"
            subtitle={
              <div>
                <em>Flower dissection</em> lesson
                <br />
                in the Plants unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/cognitive_model_answers.png"
                alt="Text: Compare the different flower species you have dissected by identifying similarities and differences. Image: Example answer table showing similarities and differences between daffodils and lilies, with photos of both flowers."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="dual-coded-explanations"
            title="Dual-coded explanations"
            description="Concise text is supported by a diagram or supporting image. Where it makes sense, videos and simulations reinforce concepts in ways that text and images are limited."
            url="http://stileapp.com/go/cognitive_example_3"
            subtitle={
              <div>
                <em>Guess to know your rocks</em> lesson
                <br />
                in the Active Earth unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/cognitive_dual_coded.png"
                alt="Text: Did you know? When lava flies through the air, weird and wonderful shapes can form! Image: Illustrated volcano with examples of volcanic rock types — cowpat bomb and Pele’s hair — alongside cartoon drawings and rock photos."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="frequent-retrieval-checks"
            title="Frequent retrieval checks"
            description="Each chapter ends with an auto-graded quiz, or check-in, that helps transfer new ideas into long-term memory before moving on."
            url="http://stileapp.com/go/cognitive_example_4"
            subtitle={
              <div>
                <em>Check-in</em> lesson
                <br />
                in the Energy Conservation – Hydropower Edition unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/cognitive_check_in_AU.png"
                alt="Text: Check-in. Image: Illustration of a fish and student character talking beside a dam photo, with multiple-choice questions shown about river ecosystems."
                objectFit="contain"
              />
            }
          />
        </Grid>
      </ExamplesSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: 'Evidence of Success',
            description: 'How Stile has a positive impact in the science classroom.',
            url: '/why-choose-stile/evidence-of-success/',
          },
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Why HQIM?',
            description: 'Why High-Quality Instructional Materials make all the difference.',
            url: '/why-choose-stile/high-quality-instructional-materials/',
          },
        ]}
      />
    </PageLayout>
  );
}

export default CognitiveLoadTheoryPage;
